import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate , useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEsNuevo, useMarcarItemVisto, useMarcarItemsLeidos } from '../contexts/NavSeenContext.js';
import { useToast } from '../components/ui/Toast.js';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { useReportInternal, type ReportInternal } from '../hooks/useReports.js';
import { Icon } from '../components/Icon.js';
import { Recorrido } from '../components/ui/Recorrido.js';
import { useRecorrido } from '../hooks/useRecorrido.js';
import { FilterRow } from '../components/ui/FilterRow.js';
import { Modal } from '../components/ui/Modal.js';
import { Input } from '../components/ui/Input.js';
import { Button } from '../components/ui/Button.js';
import { IconButton } from '../components/ui/IconButton.js';
import { SelectableCard } from '../components/ui/SelectableCard.js';
import { PillGroup, Pill, type PillTone } from '../components/PillGroup.js';
import { TimeBandedList } from '../components/common/TimeBandedList.js';
import { parseServerDate } from '../lib/date.js';
import {
  useInfiniteReports,
  useReport,
  useCreateReport,
  useDeleteReport,
  useBulkDeleteReports,
  useRegenerateReport,
  type ReportRow,
  type ReportFilters,
} from '../hooks/useReports.js';
import { useListKeyboardNav } from '../hooks/useListKeyboardNav.js';
import { ListItem } from '../components/ui/ListItem.js';
import { ListToolbar } from '../components/ui/ListToolbar.js';
import { SelectionActionBar } from '../components/ui/SelectionActionBar.js';
import { ReportClaudePanel } from '../components/reports/ReportClaudePanel.js';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return parseServerDate(iso).toLocaleDateString('es', { year: 'numeric', month: 'short', day: 'numeric' });
}

const STATUS_TONE: Record<string, PillTone> = {
  pending: 'muted',
  generating: 'amber',
  ready: 'cyan',
  failed: 'danger',
};

// ── Create Modal ───────────────────────────────────────────────────────────────

interface CreateModalProps {
  onClose: () => void;
  onCreated: (id: number) => void;
  /** Cliente precargado — viene del + de la tarjeta Reportes de un contacto. */
  contact?: { id: number; name: string } | null;
}

function CreateModal({ onClose, onCreated, contact }: CreateModalProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedType, setSelectedType] = useState<string>('web_audit');
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [urlError, setUrlError] = useState('');
  const createReport = useCreateReport();

  function handleSubmit() {
    if (!url) { setUrlError(t('reports.fields.url') + ' requerida'); return; }
    setUrlError('');
    let normalizedUrl = url.trim();
    if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
      normalizedUrl = `https://${normalizedUrl}`;
    }
    try { new URL(normalizedUrl); } catch { setUrlError(t('reports.errors.invalidUrl')); return; }

    let finalTitle = title.trim();
    if (!finalTitle) {
      try {
        const hostname = new URL(normalizedUrl).hostname.replace(/^www\./, '');
        finalTitle = `Auditoría web — ${hostname}`;
      } catch {
        finalTitle = `Auditoría web — ${normalizedUrl}`;
      }
    }

    createReport.mutate(
      { type: selectedType, title: finalTitle, inputs: { url: normalizedUrl }, ...(contact ? { contactId: contact.id } : {}) },
      { onSuccess: (data) => { onCreated(data.id); } },
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={step === 1 ? t('reports.modal.step1') : t('reports.modal.step2')}
      preventClose={createReport.isPending}
      footer={
        <>
          <Button
            variant="outline"
            tone="muted"
            size="sm"
            disabled={createReport.isPending}
            onClick={step === 1 ? onClose : () => setStep(1)}
          >
            {step === 1 ? t('common.cancel') : t('common.back')}
          </Button>
          {step === 1 ? (
            <Button
              variant="solid"
              tone="cyan"
              size="sm"
              onClick={() => setStep(2)}
            >
              {t('common.next')}
            </Button>
          ) : (
            <Button
              variant="solid"
              tone="cyan"
              size="sm"
              loading={createReport.isPending}
              onClick={handleSubmit}
            >
              {createReport.isPending ? t('common.creating') : t('reports.modal.generateBtn')}
            </Button>
          )}
        </>
      }
    >
      {step === 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <SelectableCard
            selected={selectedType === 'web_audit'}
            onSelect={() => setSelectedType('web_audit')}
            icon={<span style={{ color: 'var(--evo-cyan)', display: 'flex' }}><Icon name="activity" size={18} /></span>}
            title={t('reports.types.web_audit.label')}
            description={t('reports.types.web_audit.description')}
          />
          {(['seo_report', 'competitor_analysis'] as const).map(type => (
            <div key={type} style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', padding: '14px 16px', border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r-md)', background: 'var(--evo-bg-3)', opacity: 0.5, cursor: 'not-allowed' }}>
              <span style={{ color: 'var(--evo-fg-3)', display: 'flex', marginTop: '1px' }}>
                <Icon name="flag" size={18} />
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '13px', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)' }}>
                  {type === 'seo_report' ? 'SEO Report' : 'Competitor Analysis'}
                </div>
              </div>
              <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '10px', background: 'var(--evo-bg)', border: '1px solid var(--evo-stroke)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', whiteSpace: 'nowrap', alignSelf: 'flex-start' }}>
                {t('reports.modal.comingSoon')}
              </span>
            </div>
          ))}
        </div>
      )}

      {step === 2 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Input
            label={`${t('reports.fields.url')} *`}
            placeholder="https://example.com"
            value={url}
            onChange={setUrl}
            error={urlError || false}
            autoFocus
            fullWidth
          />
          <Input
            label={t('reports.fields.title')}
            value={title}
            onChange={setTitle}
            fullWidth
          />
          {createReport.isError && (
            <p style={{ fontSize: '12px', color: 'var(--evo-danger)', fontFamily: 'var(--evo-font)', margin: 0 }}>
              {(createReport.error)?.message ?? t('reports.errors.generation')}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

// ── Detail Panel ───────────────────────────────────────────────────────────────

interface DetailPanelProps {
  reportId: number;
  onDeleted: () => void;
}

function DetailPanel({ reportId, onDeleted }: DetailPanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: report, isLoading } = useReport(reportId);
  const deleteReport = useDeleteReport();
  const regenerateReport = useRegenerateReport();
  const [linkCopied, setLinkCopied] = useState(false);
  const [showEvents, setShowEvents] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { data: internal } = useReportInternal(report?.status === 'ready' ? reportId : null);

  function copyLink() {
    if (!report) return;
    void navigator.clipboard.writeText(`${window.location.origin}/r/${report.slug}`);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  }

  function handleDelete() {
    if (!report) return;
    deleteReport.mutate(report.id, {
      onSuccess: () => { onDeleted(); navigate('/reports'); },
    });
  }

  function handleRegenerate() {
    if (!report) return;
    if (!confirm(t('reports.regenerateConfirm'))) return;
    regenerateReport.mutate(report.id);
  }

  if (isLoading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--evo-fg-3)', fontSize: '13px', fontFamily: 'var(--evo-font)' }}>
        {t('common.loading')}
      </div>
    );
  }

  if (!report) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--evo-fg-3)', fontSize: '13px', fontFamily: 'var(--evo-font)' }}>
        {t('common.noResults')}
      </div>
    );
  }

  const isGenerating = report.status === 'pending' || report.status === 'generating';

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
      {/* Action row */}
      <div style={{ height: 'var(--evo-toolbar-height)', padding: '0 var(--evo-content-pad-x) 0 var(--evo-space-5)', borderBottom: '1px solid var(--evo-stroke)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
          <span style={{ fontSize: 'var(--evo-fs-body-lg)', fontWeight: 'var(--evo-fw-bold)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {report.title}
          </span>
          <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '10px', background: 'var(--evo-bg-3)', border: '1px solid var(--evo-stroke)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', fontWeight: 500, flexShrink: 0 }}>
            {t(`reports.types.${report.type}.label`)}
          </span>
          <Pill tone={STATUS_TONE[report.status] ?? 'muted'} size="xs">
            {t(`reports.status.${report.status}`)}
          </Pill>
        </div>
        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 'var(--evo-space-2)', flexShrink: 0, alignItems: 'center' }}>
          {!confirmDelete && (
            <>
              {report.status === 'ready' && (
                <>
                  {/* Iconos con tooltip, como el resto de la app: la fila de
                      acciones de un detalle no se lee, se reconoce. */}
                  <IconButton
                    size="md" variant="outline" tone="cyan"
                    icon={<Icon name={linkCopied ? 'check' : 'link'} size={15} />}
                    ariaLabel={linkCopied ? t('reports.linkCopied') : t('reports.actions.copyLink')}
                    tooltip={linkCopied ? t('reports.linkCopied') : t('reports.actions.copyLink')}
                    onClick={copyLink}
                  />
                  <IconButton
                    size="md" variant="outline" tone="muted"
                    icon={<Icon name="external" size={15} />}
                    ariaLabel={t('reports.actions.viewPublic')}
                    tooltip={t('reports.actions.viewPublic')}
                    onClick={() => window.open(`/r/${report.slug}`, '_blank')}
                  />
                </>
              )}
              <IconButton
                size="md" variant="outline" tone="muted"
                icon={<Icon name="refresh" size={15} />}
                ariaLabel={t('reports.actions.regenerate')}
                tooltip={t('reports.actions.regenerate')}
                loading={regenerateReport.isPending}
                onClick={handleRegenerate}
              />
              <IconButton
                size="md" variant="outline" tone="danger"
                icon={<Icon name="trash" size={15} />}
                ariaLabel={t('reports.actions.delete')}
                tooltip={t('reports.actions.delete')}
                onClick={() => setConfirmDelete(true)}
              />
            </>
          )}
          {confirmDelete && (
            <>
              <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-danger)', fontFamily: 'var(--evo-font)', whiteSpace: 'nowrap' }}>
                {t('reports.deleteConfirm')}
              </span>
              <Button size="sm" variant="solid" tone="danger" loading={deleteReport.isPending} disabled={deleteReport.isPending} onClick={handleDelete}>{t('reports.actions.delete')}</Button>
              <Button size="sm" variant="outline" tone="muted" onClick={() => setConfirmDelete(false)}>{t('common.cancel')}</Button>
            </>
          )}
        </div>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
        {/* Metadata row */}
        <div style={{ marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
          <MetaItem label={t('reports.fields.createdAt')} value={formatDate(report.createdAt)} />
          {report.generatedAt && <MetaItem label={t('reports.fields.generatedAt')} value={formatDate(report.generatedAt)} />}
          {report.status === 'ready' && (
            <>
              <MetaItem label={t('reports.stats.views_other', { count: report.publicViews })} value={String(report.publicViews)} />
              <MetaItem label={t('reports.stats.downloads_other', { count: report.pdfDownloads })} value={String(report.pdfDownloads)} />
            </>
          )}
        </div>

        {isGenerating && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '60px 20px', color: 'var(--evo-fg-3)' }}>
            <SpinnerIcon />
            <p style={{ fontSize: '14px', fontFamily: 'var(--evo-font)', fontWeight: 600, color: 'var(--evo-fg-2)', margin: 0 }}>
              {t('reports.generating.message')}
            </p>
            <p style={{ fontSize: '12px', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)', margin: 0 }}>
              {t('reports.generating.subtext')}
            </p>
          </div>
        )}

        {report.status === 'failed' && (
          <div style={{ padding: '20px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--evo-r-md)' }}>
            <p style={{ fontSize: '13px', fontFamily: 'var(--evo-font)', color: 'var(--evo-danger)', fontWeight: 600, margin: '0 0 8px' }}>
              {t('reports.errors.generation')}
            </p>
            {report.lastError && (
              <pre style={{ fontSize: '11px', fontFamily: 'monospace', color: 'var(--evo-fg-3)', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {report.lastError}
              </pre>
            )}
          </div>
        )}

        {report.status === 'ready' && (
          <>
            <iframe
              src={`${window.location.origin}/r/${report.slug}`}
              style={{ width: '100%', minHeight: '800px', border: 'none', borderRadius: 'var(--evo-r-md)' }}
              title={report.title}
            />
            <InternalPanel
              internal={internal}
              reportId={reportId}
              slug={report.slug}
              showEvents={showEvents}
              onToggleEvents={() => setShowEvents(v => !v)}
            />
          </>
        )}
      </div>
    </div>
  );
}

interface InternalPanelProps {
  internal: ReportInternal | undefined;
  reportId: number;
  slug: string;
  showEvents: boolean;
  onToggleEvents: () => void;
}

function StatBox({ label, value }: { label: string; value: string | number }) {
  return (
    <div style={{ flex: 1, minWidth: '100px', padding: '14px 16px', background: 'var(--evo-bg-3)', borderRadius: 'var(--evo-r-md)', border: '1px solid var(--evo-stroke)' }}>
      <div style={{ fontSize: '22px', fontWeight: 700, fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-1)', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '11px', color: 'var(--evo-fg-4)', marginTop: '4px', fontFamily: 'var(--evo-font)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
    </div>
  );
}

function InternalPanel({ internal, reportId, slug: _slug, showEvents, onToggleEvents }: InternalPanelProps) {
  const { t } = useTranslation();
  const stats = internal?.stats;
  const events = internal?.recentEvents ?? [];

  function downloadJson() {
    window.open(`/api/reports/${reportId}/raw-json`, '_blank');
  }

  const eventTypeLabel = (type: string) => {
    const map: Record<string, string> = {
      view_open: t('reports.internal.eventTypes.view_open'),
      technical_expand: t('reports.internal.eventTypes.technical_expand'),
      cta_click: t('reports.internal.eventTypes.cta_click'),
      time_on_page: t('reports.internal.eventTypes.time_on_page'),
    };
    return map[type] ?? type;
  };

  return (
    <div style={{ marginTop: '24px', borderTop: '2px solid var(--evo-stroke)', paddingTop: '20px' }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
          {t('reports.internal.title')}
        </span>
        <Button variant="link" tone="cyan" size="sm" onClick={downloadJson}>
          {t('reports.internal.downloadJson')}
        </Button>
      </div>

      {!stats || stats.totalViews === 0 ? (
        <p style={{ fontSize: '13px', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>{t('reports.internal.noData')}</p>
      ) : (
        <>
          {/* Stats grid */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <StatBox label={t('reports.internal.totalViews')} value={stats.totalViews} />
            <StatBox label={t('reports.internal.uniqueVisitors')} value={stats.uniqueVisitors} />
            <StatBox label={t('reports.internal.technicalExpands')} value={stats.technicalExpands} />
            <StatBox label={t('reports.internal.ctaClicks')} value={stats.ctaClicks} />
          </div>
          {/* Engagement row */}
          <div style={{ fontSize: '12px', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', display: 'flex', gap: '16px', marginBottom: '16px' }}>
            {stats.lastViewAgo && (
              <span>{t('reports.internal.lastView')}: <strong style={{ color: 'var(--evo-fg-2)' }}>{stats.lastViewAgo}</strong></span>
            )}
            {stats.avgTimeOnPage != null && (
              <span>{t('reports.internal.avgTime')}: <strong style={{ color: 'var(--evo-fg-2)' }}>{t('reports.internal.avgTimeValue', { seconds: stats.avgTimeOnPage })}</strong></span>
            )}
          </div>
          {/* Events toggle */}
          {events.length > 0 && (
            <div>
              <div style={{ marginBottom: '8px' }}>
                <Button variant="link" tone="muted" size="sm" onClick={onToggleEvents}>
                  {showEvents ? t('reports.internal.eventsHide') : `${t('reports.internal.eventsToggle')} (${events.length})`}
                </Button>
              </div>
              {showEvents && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {events.map(ev => (
                    <div key={ev.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '6px 10px', background: 'var(--evo-bg-2)', borderRadius: '6px', fontSize: '11px', fontFamily: 'var(--evo-font)' }}>
                      <span style={{ color: 'var(--evo-fg-4)', whiteSpace: 'nowrap', minWidth: '140px' }}>
                        {new Date(ev.createdAt).toLocaleString()}
                      </span>
                      <span style={{ color: 'var(--evo-fg-2)', fontWeight: 500, minWidth: '120px' }}>{eventTypeLabel(ev.eventType)}</span>
                      {ev.hashedIpShort && <span style={{ color: 'var(--evo-fg-4)', fontFamily: 'monospace' }}>{ev.hashedIpShort}</span>}
                      {ev.userAgentSimple && <span style={{ color: 'var(--evo-fg-4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.userAgentSimple}</span>}
                      {ev.metadata?.seconds != null && <span style={{ color: 'var(--evo-fg-4)' }}>{String(ev.metadata.seconds)}s</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ fontSize: '12px', fontFamily: 'var(--evo-font)' }}>
      <span style={{ color: 'var(--evo-fg-3)' }}>{label}: </span>
      <span style={{ color: 'var(--evo-fg-2)', fontWeight: 500 }}>{value}</span>
    </div>
  );
}

function SpinnerIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="var(--evo-cyan)" strokeWidth="2.5" strokeLinecap="round">
      <circle cx="16" cy="16" r="12" strokeOpacity="0.2" />
      <path d="M16 4a12 12 0 0 1 12 12">
        <animateTransform attributeName="transform" type="rotate" from="0 16 16" to="360 16 16" dur="0.8s" repeatCount="indefinite" />
      </path>
    </svg>
  );
}

// ── Reports List Panel ─────────────────────────────────────────────────────────

// ── List panel ─────────────────────────────────────────────────────────────────

interface ReportsListPanelProps {
  selectedId: number | null;
  onSelect: (id: number) => void;
  filters: ReportFilters;
  search: string;
  selectedIds: Set<number>;
  onToggleId: (id: number, selected: boolean) => void;
  onSelectAllVisible: (ids: number[]) => void;
  onClearSelection: () => void;
}

function ReportsListPanel({ selectedId, onSelect, filters, search, selectedIds, onToggleId, onSelectAllVisible, onClearSelection }: ReportsListPanelProps) {
  const { t } = useTranslation();
  const esNuevo = useEsNuevo('reports');
  const marcarVisto = useMarcarItemVisto('reports');
  const activeFilters = { ...filters, ...(search ? { search } : {}) };
  const [collapsedBands, setCollapsedBands] = useState<Set<string>>(new Set());
  const sentinelRef = useRef<HTMLDivElement>(null);

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteReports(activeFilters);

  const reports = useMemo(
    () => data?.pages.flatMap(p => p.rows) ?? [],
    [data],
  );
  const total = data?.pages[0]?.total ?? 0;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useListKeyboardNav(reports, selectedId, id => onSelect(id as number));

  return (
    <div style={{ width: 'clamp(300px, 33%, 480px)', flexShrink: 0, borderRight: '1px solid var(--evo-stroke)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* List header */}
      <ListToolbar
        allSelected={reports.length > 0 && reports.every(r => selectedIds.has(r.id))}
        someSelected={selectedIds.size > 0 && !reports.every(r => selectedIds.has(r.id))}
        onSelectAll={sel => {
          if (sel) onSelectAllVisible(reports.map(r => r.id));
          else onClearSelection();
        }}
        onExpandAll={() => setCollapsedBands(new Set())}
        onCollapseAll={() => setCollapsedBands(new Set(['today', 'yesterday', 'this_week', 'this_month', 'older_2_3_months', 'older']))}
        expandLabel={t('email.expandAll')}
        collapseLabel={t('email.collapseAll')}
      />

      {/* List items */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {isLoading && (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--evo-fg-3)', fontSize: '12px', fontFamily: 'var(--evo-font)' }}>
            {t('common.loading')}
          </div>
        )}
        {!isLoading && reports.length === 0 && (
          <div style={{ padding: '48px 16px', textAlign: 'center' }}>
            <Icon name="activity" size={24} />
            <p style={{ fontSize: '13px', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', margin: '8px 0 4px' }}>{t('reports.noReports')}</p>
            <p style={{ fontSize: '12px', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', margin: 0 }}>{t('reports.createFirst')}</p>
          </div>
        )}
        {!isLoading && reports.length > 0 && (
          <TimeBandedList<ReportRow>
            items={reports}
            getId={r => r.id}
            getTimestamp={r => r.createdAt}
            collapsedBands={collapsedBands}
            onToggleBand={band => setCollapsedBands(prev => {
              const next = new Set(prev);
              if (next.has(band)) next.delete(band); else next.add(band);
              return next;
            })}
            checkedIds={selectedIds}
            onBandCheck={(ids, allChecked) => { ids.forEach(id => onToggleId(id, !allChecked)); }}
            renderItem={report => {
              const inputs = (() => { try { return JSON.parse(report.inputsJson) as { url?: string }; } catch { return {}; } })();
              return (
                <ListItem
                  key={report.id}
                  itemId={report.id}
                  tourId={report.id === reports[0]?.id ? 'reports-fila' : undefined}
                  title={report.title}
                  {...(inputs.url ? { subtitle: inputs.url.replace(/^https?:\/\//, '') } : {})}
                  meta={formatDate(report.createdAt)}
                  badges={[
                    <Pill key="status" tone={STATUS_TONE[report.status] ?? 'muted'} size="xs">
                      {t(`reports.status.${report.status}`)}
                    </Pill>,
                  ]}
                  selected={selectedIds.has(report.id)}
                  onSelect={sel => onToggleId(report.id, sel)}
                  // Reportes no lleva "pendiente" encima de la fila: un reporte
                  // listo no espera nada por sí mismo. Lo que se marca es la
                  // NOVEDAD — los que llegaron desde la última vez que entraste,
                  // comparando contra la marca de agua anterior a esta visita.
                  //
                  // Ojo con report.lastViewedAt: ese campo dice si lo abrió el
                  // PROSPECTO, no vos. Usarlo acá repetiría el bug que este
                  // trabajo vino a arreglar.
                  unread={esNuevo(report.createdAt, report.id)}
                  active={selectedId === report.id}
                  onClick={() => { marcarVisto(report.id); onSelect(report.id); }}
                  density="default"
                />
              );
            }}
          />
        )}
        {isFetchingNextPage && (
          <div style={{ padding: '12px 16px', textAlign: 'center', color: 'var(--evo-fg-3)', fontSize: '12px', fontFamily: 'var(--evo-font)' }}>
            {t('common.loading')}
          </div>
        )}
        <div ref={sentinelRef} style={{ height: '1px' }} />
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export function ReportsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: idParam } = useParams<{ id?: string }>();
  const parsedId = idParam ? parseInt(idParam) : NaN;
  const isSlugParam = idParam != null && !Number.isFinite(parsedId);
  // Old ContentPlan JSON only stamped reportSlug — "View report" can still land
  // here with a slug instead of a numeric id. Resolve it, then canonicalize the
  // URL once we know the real numeric id (selectedId stays number-typed for the
  // rest of the page: bulk-select Set<number>, active-row comparisons, etc.).
  const selectedId = isSlugParam ? null : (idParam ? parsedId : null);
  const { data: resolvedBySlug } = useReport(isSlugParam ? idParam! : null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  // “+” de Relacionados (2026-08-29): ?nuevo=1&contactId&contactName abre el
  // flujo de crear con el cliente precargado y limpia los params.
  const [precargado, setPrecargado] = useState<{ id: number; name: string } | null>(null);
  useEffect(() => {
    if (searchParams.get('nuevo') !== '1') return;
    const cid = parseInt(searchParams.get('contactId') ?? '');
    const cname = searchParams.get('contactName') ?? '';
    if (Number.isFinite(cid)) setPrecargado({ id: cid, name: cname || `#${cid}` });
    setShowCreateModal(true);
    const next = new URLSearchParams(searchParams);
    next.delete('nuevo'); next.delete('contactId'); next.delete('contactName');
    setSearchParams(next, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<ReportFilters>({});
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const bulkDelete = useBulkDeleteReports();
  const regenerate = useRegenerateReport();
  const marcarLeidos = useMarcarItemsLeidos('reports');
  const toast = useToast();
  const [marcando, setMarcando] = useState(false);
  const [regenerando, setRegenerando] = useState(false);

  const hasActiveFilters = !!(filters.type || filters.status || search);

  useEffect(() => {
    if (resolvedBySlug) navigate(`/reports/${resolvedBySlug.id}`, { replace: true });
  }, [resolvedBySlug, navigate]);

  const { setOverride } = useTopBarCtx();
  useEffect(() => {
    setOverride({
      title: t('reports.pageTitle'),
      heroIcon: 'reports',
      heroIconColor: 'var(--evo-steel)',
      bottomActions: (
        <div data-tour-id="reports-nuevo" style={{ display: 'flex' }}>
          <PillGroup size="md">
            <Pill tone="cyan" onClick={() => setShowCreateModal(true)}>
              {t('reports.newButton')}
            </Pill>
          </PillGroup>
        </div>
      ),
    });
    return () => setOverride({});
  }, [setOverride, t]);

  function handleSelect(id: number) {
    navigate(`/reports/${id}`);
  }

  function handleCreated(id: number) {
    setShowCreateModal(false);
    navigate(`/reports/${id}`);
  }

  function clearFilters() {
    setSearch('');
    setFilters({});
  }

  function handleToggleId(id: number, sel: boolean) {
    setSelectedIds(prev => { const next = new Set(prev); if (sel) next.add(id); else next.delete(id); return next; });
  }

  function handleSelectAllVisible(ids: number[]) {
    setSelectedIds(new Set(ids));
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  async function handleBulkLeido(leido: boolean) {
    const ids = Array.from(selectedIds);
    setMarcando(true);
    try {
      await marcarLeidos(ids, leido);
      toast.success(t(leido ? 'reports.bulk.markedRead' : 'reports.bulk.markedUnread', { count: ids.length }));
      clearSelection();
    } catch {
      toast.error(t('reports.bulk.markFailed'));
    } finally {
      setMarcando(false);
    }
  }

  async function handleBulkRegenerar() {
    const ids = Array.from(selectedIds);
    setRegenerando(true);
    const resultados = await Promise.allSettled(ids.map(id => regenerate.mutateAsync(id)));
    setRegenerando(false);
    const ok = resultados.filter(r => r.status === 'fulfilled').length;
    if (ok === ids.length) toast.success(t('reports.bulk.regenerated', { count: ok }));
    else toast.error(t('reports.bulk.regeneratePartial', { done: ok, count: ids.length }));
    clearSelection();
  }

  // ANTES del return: un hook debajo de un return condicional cambia la
  // cantidad de hooks entre renders y React tira la pantalla entera.
  const recorrido = useRecorrido('reports', { hayDatos: true, listo: true });

  return (
    <div className="flex flex-col h-full animate-fade-blur-in">
      <Recorrido
        pasos={recorrido.pasos}
        abierto={recorrido.abierto}
        onCerrar={recorrido.cerrar}
        onTerminar={recorrido.terminar}
      />
      {/* Filter bar */}
      <FilterRow>
        <FilterRow.Search
          value={search}
          onChange={setSearch}
          onSubmit={() => {}}
          onClear={() => setSearch('')}
          placeholder={t('reports.searchPlaceholder')}
        />

        <div data-tour-id="reports-tipo" style={{ display: 'flex' }}>
        <FilterRow.Dropdown<string>
          value={filters.type ?? ''}
          placeholder={t('reports.filters.allTypes')}
          options={[
            { value: '', label: t('reports.filters.allTypes') },
            { value: 'web_audit', label: t('reports.types.web_audit.label') },
            { value: 'data_query', label: t('reports.types.data_query.label') },
          ]}
          onChange={val => setFilters(f => { const { type: _t, ...rest } = f; return val ? { ...rest, type: val } : rest; })}
        />
        </div>

        <div data-tour-id="reports-estado" style={{ display: 'flex' }}>
        <FilterRow.Dropdown<string>
          value={filters.status ?? ''}
          placeholder={t('reports.filters.allStatuses')}
          options={[
            { value: '', label: t('reports.filters.allStatuses') },
            { value: 'pending', label: t('reports.status.pending') },
            { value: 'generating', label: t('reports.status.generating') },
            { value: 'ready', label: t('reports.status.ready') },
            { value: 'failed', label: t('reports.status.failed') },
          ]}
          onChange={val => setFilters(f => { const { status: _s, ...rest } = f; return val ? { ...rest, status: val } : rest; })}
        />
        </div>

        {hasActiveFilters && (
          <FilterRow.Action label={t('common.clearFilters')} onClick={clearFilters} />
        )}
      </FilterRow>

      <SelectionActionBar
        visible={selectedIds.size > 0}
        countLabel={t('reports.selected', { count: selectedIds.size })}
        onClearSelection={clearSelection}
      >
        {/* Las mismas acciones que el detalle, menos las que son de a uno
            (copiar enlace, ver la página pública). */}
        <SelectionActionBar.Action
          label={t('reports.actions.markRead')}
          loading={marcando}
          onClick={() => { void handleBulkLeido(true); }}
        />
        <SelectionActionBar.Action
          label={t('reports.actions.markUnread')}
          loading={marcando}
          onClick={() => { void handleBulkLeido(false); }}
        />
        <SelectionActionBar.Divider />
        {/* Reescribe el contenido y vuelve a gastar IA: con confirmación, como
            el botón del detalle. */}
        <SelectionActionBar.Action
          label={t('reports.actions.bulkRegenerate')}
          confirmRequired
          loading={regenerando}
          onClick={() => { void handleBulkRegenerar(); }}
        />
        <SelectionActionBar.Divider />
        <SelectionActionBar.Action
          label={t('reports.actions.bulkDelete')}
          tone="danger"
          confirmRequired
          loading={bulkDelete.isPending}
          onClick={() => {
            const ids = Array.from(selectedIds);
            bulkDelete.mutate(ids, {
              onSuccess: () => {
                if (selectedId !== null && selectedIds.has(selectedId)) navigate('/reports');
                clearSelection();
              },
            });
          }}
        />
      </SelectionActionBar>

      {/* 3-column layout: list | detail | claude */}
      <div data-tour-id="reports-lista" style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <ReportsListPanel
          selectedId={selectedId}
          onSelect={handleSelect}
          filters={filters}
          search={search}
          selectedIds={selectedIds}
          onToggleId={handleToggleId}
          onSelectAllVisible={handleSelectAllVisible}
          onClearSelection={clearSelection}
        />

        {selectedId !== null ? (
          <DetailPanel
            reportId={selectedId}
            onDeleted={() => {}}
          />
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px', color: 'var(--evo-fg-3)' }}>
            <Icon name="activity" size={28} />
            <p style={{ fontSize: '13px', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)', margin: 0 }}>{t('reports.selectOrCreate')}</p>
          </div>
        )}

        <ReportClaudePanel reportId={selectedId} />
      </div>

      {showCreateModal && (
        <CreateModal
          contact={precargado}
          onClose={() => { setShowCreateModal(false); setPrecargado(null); }}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
